import React, {useEffect, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useTranslation } from "react-i18next";
import {Icon} from "@blueprintjs/core";

// Libs
import Button from "../common/components/button";
import { primaryColor } from "../common/libs/theme-lib";

// Components
import SelectInput from "../common/components/select-input";
import RenderContent from "../common/components/render-content";
import DateInput from "../common/components/date-input";
import TextInput from "../common/components/text-input";

// Actions 
import { readDocumentation, downloadDocumentation } from "../documentation/documentation-actions";
import { readClients } from "../clients/clients-actions";

// Selectors 
import { documentationSelector } from "./documentation-selectors";
import {clientsSelector} from "../clients/clients-selectors";

const documentationPageSelector = createSelector(
    documentationSelector,
    clientsSelector,
    (documentation, clients) => {
        documentation = documentation && documentation.toJS();
        documentation = (Array.isArray(documentation) && documentation) || [];
        return {
            clients: clients?.toJS()?.map(c => ({value: c._id, label: c.name})) || [],
            documentation: documentation,
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readClients: () => dispatch(readClients()),
        readDocumentation: (filters) => dispatch(readDocumentation(filters)),
        downloadDocumentation: (documentation, fileName) => dispatch(downloadDocumentation(documentation, fileName))
    }
};

const AdminDocumentationPage = () => {

    const { documentation, clients } = useSelector(state => documentationPageSelector(state));

    const { readClients, readDocumentation, downloadDocumentation } = useReduxDispatch();

    // States 
    const [searchName, setSearchName] = useState(null);
    const [client, setClient] = useState(null);
    const [fromStartDate, setFromStartDate] = useState(null);
    const [toStartDate, setToStartDate] = useState(null);
    const [fromEndDate, setFromEndDate] = useState(null);
    const [toEndDate, setToEndDate] = useState(null);

    // state of showWelcomeMessage
    const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);

    const filters = {
        searchName,
        client,
        fromStartDate,
        toStartDate,
        fromEndDate,
        toEndDate,
    }

    // This use effect executed one time
    useEffect(() => {
        readClients();
    }, []);

    //i18n
    const {t} = useTranslation("adminDocumentation");

   return (
       <>
           <div style={{
               display: "flex",
               alignItems: "flex-end",
               paddingBottom: "15px"
           }}>
               <div style={{flex: 1}}>
                   <div style={{
                       color: primaryColor,
                       fontSize: 28,
                       fontWeight: "bold",
                       marginBottom: 10
                   }}>{t("title")}</div>
                   <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
               </div>
               <TextInput
                    label={t("name")}
                    style={{marginRight: 15, minWidth: 200, marginLeft: 15}}
                    value={searchName}
                    setValue={setSearchName}
               />
               <SelectInput
                    options={clients}
                    name={"clientSelector"}
                    label={t("client")}
                    onChange={(newClient) => setClient(newClient.value)}
                    value={clients.find(c => c.value === client)}
                    style={{marginRight: 15}}
                />
               <DateInput
                   labelText={t("fromStartDate")}
                   inputWrapperStyle={{marginRight: 15}}
                   value={fromStartDate}
                   onChange={newDate => setFromStartDate(newDate?.startOf("day") || newDate)}
               />
               <DateInput
                   labelText={t("toStartDate")}
                   inputWrapperStyle={{marginRight: 15}}
                   value={toStartDate}
                   onChange={newDate => setToStartDate(newDate?.endOf("day") || newDate)}
               />
               <DateInput
                   labelText={t("fromEndDate")}
                   inputWrapperStyle={{marginRight: 15}}
                   value={fromEndDate}
                   onChange={newDate => setFromEndDate(newDate?.startOf("day") || newDate)}
               />
               <DateInput
                   labelText={t("toEndDate")}
                   inputWrapperStyle={{marginRight: 15}}
                   value={toEndDate}
                   onChange={newDate => setToEndDate(newDate?.endOf("day") || newDate)}
               />
               <Button
                   text={t("common:actions.search")}
                   action={() => {
                        readDocumentation(filters);
                        setShowWelcomeMessage(false);
                   }}
                   intent="primary"
               />
           </div>
           <RenderContent
                nameCollection={t("nameCollection")}
                data={documentation}
                showWelcomeMessage={showWelcomeMessage}
                renderHeader={true}
                renderHeaderText="adminDocumentation:totalDocumentation" 
                renderHeaderValues={{count: documentation.length}}
                renderHeaderComponents={[<strong>number</strong>]}
                config={{
                    sortable: true,
                    initialState: {sortBy: [{id: "endDate", desc: false}]},
                    conditionalRowStyle: {
                        dataField: 'error',
                        value: true,
                        style: {backgroundColor: '#ffd3d3'}
                    }
                }}
                columns={[{
                    id: "name",
                    Header: t("name"),
                    accessor: "name",
                    type: "string"
                },
                {
                    id: "ClientName",
                    Header: t("clientName"),
                    accessor: "clientName",
                    type: "string"
                },
                {
                    id: "startDate",
                    Header: t("startDate"),
                    accessor: "startDate",
                    type: "date"
                },
                {
                    id: "endDate",
                    Header: t("endDate"),
                    accessor: "endDate",
                    type: "date"
                },
                {
                    id: "type",
                    Header: t("type"),
                    accessor: "extension",
                    type: "string"
                },
                {
                    id: "size",
                    Header: t("size"),
                    accessor: row => (Math.trunc(row.size/1024) + " KB"),
                    type: "string"
                },
                {
                    id: "actions",
                    Header: t("download"),
                    accessor: "_id",
                    type: "function",
                    disableSortBy: true,
                    style: {
                        maxWidth: 60,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    },
                    nodeContent: (value) => {
                        const document = documentation.find((doc) => doc._id === value)
                        return (
                            <Icon 
                                style={{ cursor: "pointer" }}
                                icon="download"
                                iconSize={20}
                                onClick={() => downloadDocumentation(document._id, document.fileName)}
                            />
                        );
                    }
                }]}
           />
       </>
   );
};

export default AdminDocumentationPage;